import {Color} from '@benchling/nucleus-ui';
import {Grid} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import AntibodyFormatCreationPane from 'antibodies/format/AntibodyFormatCreationPane';
import Antibody, {AntibodyProps} from 'antibodies/visualization/Antibody';
import BenchlingConfig from 'BenchlingConfig';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import PlainSearchInput, {ClearType, BorderRadius} from 'ui-library/PlainSearchInput';
import {InputChangeEvent} from 'util/Types';

export type AntibodyTileProps = AntibodyProps & {disabled: boolean};

export interface AntibodyFormatPickerProps {
  antibodyTileProps: Array<AntibodyTileProps>;
  disabled: boolean;
  selectedFormat: string | null;
  setSelectedFormat: (formatId: string) => void;
  enableCreateNew?: boolean;
  onFormatCreated?: (formatId: string) => Promise<void>;
}

interface FormatTileProps {
  formatId: string;
  label: string;
  onClick: () => void;
  isSelected: boolean;
  disabled?: boolean;
  isHovered: boolean;
  setHoveredFormatId: (formatId: string | null) => void;
  children: React.ReactNode;
  wrapperClassName?: string;
  labelClassName?: string;
}

function FormatTile({
  formatId,
  label,
  onClick,
  isSelected,
  disabled = false,
  isHovered,
  setHoveredFormatId,
  children,
  wrapperClassName,
  labelClassName,
}: FormatTileProps): JSX.Element {
  return (
    <div role="gridcell" aria-selected={isSelected} aria-label={`${label} format`}>
      <button
        className={classNames(css(styles.button), {[css(styles.disabledButton)]: disabled})}
        onClick={onClick}
        onMouseEnter={() => {
          setHoveredFormatId(formatId);
        }}
        onMouseLeave={() => {
          setHoveredFormatId(null);
        }}
        type="button"
        disabled={disabled}
      >
        <div
          className={classNames(css(styles.formatTileGlyphWrapper), wrapperClassName, {
            [css(styles.hoveredTileGlyphWrapper)]: !isSelected && isHovered,
            [css(styles.disabledTileGlyphWrapper)]: disabled,
            [css(styles.selectedFormat)]: isSelected,
          })}
        >
          {children}
        </div>
        <div className={classNames(css(styles.buttonLabel), labelClassName)}>{label}</div>
      </button>
    </div>
  );
}

export default function AntibodyFormatPicker({
  antibodyTileProps: antibodyProps,
  disabled,
  selectedFormat,
  setSelectedFormat,
  enableCreateNew = true,
  onFormatCreated,
}: AntibodyFormatPickerProps): JSX.Element {
  const [query, setQuery] = useState<string>('');
  // Hack to get around the fact that Aphrodite doesn't support nested styling
  const [hoveredFormatId, setHoveredFormatId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const antibodyPropsMatchingQuery = antibodyProps.filter((props) =>
    props.format.name.toLowerCase().includes(query.toLowerCase())
  );

  if (showCreateModal) {
    return (
      <AntibodyFormatCreationPane
        onRequestClose={() => {
          setShowCreateModal(false);
        }}
        onCreate={async (newFormatId) => {
          setShowCreateModal(false);
          await onFormatCreated?.(newFormatId);
        }}
      />
    );
  }

  return (
    <>
      <Grid mb="sm">
        <Grid.Col span={6}>
          <PlainSearchInput
            value={query}
            placeholder="Search by format name"
            onChange={(e: InputChangeEvent) => {
              setQuery(e.target.value);
            }}
            onEscape={() => {
              setQuery('');
            }}
            clearType={ClearType.X}
            borderRadius={BorderRadius.LARGE}
          />
        </Grid.Col>
      </Grid>
      <div className={css(styles.picker)}>
        {enableCreateNew && (
          <FormatTile
            formatId="create-new-format"
            label="Create new"
            onClick={() => {
              if (BenchlingConfig.ANTIBODY_ENABLE_VERITAS_FORMAT_PREVIEW) {
                setShowCreateModal(true);
              }
            }}
            isSelected={false}
            isHovered={hoveredFormatId === 'create-new-format'}
            setHoveredFormatId={setHoveredFormatId}
            wrapperClassName={css(styles.createNewGlyphWrapper)}
            labelClassName={css(styles.createNewButtonLabel)}
            disabled={!BenchlingConfig.ANTIBODY_ENABLE_VERITAS_FORMAT_PREVIEW}
          >
            <BenchlingIcon icon="plus" />
          </FormatTile>
        )}
        {!antibodyPropsMatchingQuery.length && (
          <div className={css(styles.noResultsMessageContainer)}>No formats found</div>
        )}
        {antibodyPropsMatchingQuery.map((props) => (
          <FormatTile
            key={props.format.id}
            formatId={props.format.id}
            label={props.format.name}
            onClick={() => {
              setSelectedFormat(props.format.id);
            }}
            isSelected={props.format.id === selectedFormat}
            isHovered={hoveredFormatId === props.format.id}
            disabled={disabled || props.disabled}
            setHoveredFormatId={setHoveredFormatId}
            wrapperClassName={css(styles.formatGlyphWrapper)}
          >
            <Antibody {...props} />
          </FormatTile>
        ))}
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  picker: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    padding: '16px',
    border: `1px solid ${Color.GRAY_200}`,
    borderRadius: '4px',
    minHeight: '160px',
    maxHeight: '300px',
    overflowY: 'auto',
    position: 'relative',
  },
  button: {
    backgroundColor: Color.WHITE,
    border: 'none',
    flex: '0 0 90px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '4px',
    borderRadius: '4px',
    ':hover': {
      backgroundColor: Color.GRAY_100,
    },
  },
  disabledButton: {
    cursor: 'not-allowed',
  },
  buttonLabel: {
    display: 'flex',
    justifyContent: 'center',
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  createNewButtonLabel: {
    color: Color.BLUE_450,
  },
  formatTileGlyphWrapper: {
    border: `1px solid ${Color.GRAY_100}`,
    borderRadius: '4px',
    width: '90px',
    height: '90px',
    flex: '0 0 90px',
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    padding: '8px',
  },
  hoveredTileGlyphWrapper: {
    borderColor: Color.GRAY_200,
  },
  disabledTileGlyphWrapper: {
    opacity: 0.75,
    backgroundColor: Color.GRAY_100,
    // 'border: none' would slightly change the glyph size because the wrapper element has fixed size
    border: `1px solid ${Color.GRAY_100}`,
  },
  formatGlyphWrapper: {
    backgroundColor: Color.GRAY_50,
  },
  createNewGlyphWrapper: {
    backgroundColor: Color.WHITE,
    color: Color.BLUE_500,
    fontSize: '32px',
  },
  selectedFormat: {
    borderColor: Color.BLUE_500,
  },
  noResultsMessageContainer: {
    // Center text in picker element even if the "Create new" button is present
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: Color.GRAY_400,
    pointerEvents: 'none', // Otherwise the "Create new" button will not be selectable
  },
});
